import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import { CustomModal, TextField } from '../ui';
import { InfoIcon, PlusIcon, SearchIcon } from '../../icons';
import { ExportList, ExportListMobile } from './exportList';
import { PageList, PageListMobile } from './pageList';
import { UserForm } from '../user/userForm';
import { exportOptions } from '../../utils/exports';
import { ListDashesIcon } from '../../icons/listDashesIcon';
import ColumnList from './columnList';
import usePersonStore from '../../store/personStore';

export const HeaderToolBar = ({
  searchClassName,
  onUserAdded,
  searchTerm,
  configurations = {},
  handleSearch = () => {},
  showPageList = false,
  handleExport,
  handleAddMultipleUsers,
  handleColumnListClick = () => {},
  columnList = [],
  openColumnListModal,
  handleColumnSave,
  selectedColumns = [],
  dataPerPageCount = 10,
  handlePerPageDataFetch,
  showAddUser = false,
}) => {
  const userData = usePersonStore((store) => store.userData);
  const hasAdminRole = userData && userData.isAdmin;

  const { dataSize } = configurations;
  const [openModal, setOpenModal] = useState(false);
  const [sizeListPopover, setSizeList] = useState(false);
  const [sizeListPopoverMobile, setSizeListMobile] = useState(false);
  const [searchText, setSearchText] = useState('');

  const [popoverExport, setPopoverExport] = useState(false);
  const [popoverExportMobile, setPopoverExportMobile] = useState(false);

  const debouncedHandleSearch = useMemo(() => {
    return debounce(handleSearch, 600);
  }, []);

  return (
    <div className={'grid lg:grid-cols-3 max-lg:gap-4'}>
      <TextField
        wrapperClass={clsx('flex-1', searchClassName)}
        className={
          'h-full rounded-full text-body border-none items-center px-5 lg:py-4 py-3'
        }
        placeholder={'Search'}
        value={searchText}
        onChange={(e) => {
          setSearchText(e.target.value);
          debouncedHandleSearch(e.target.value);
        }}
        icon={<SearchIcon />}
      />
      <div
        className={
          'flex lg:col-span-2 gap-8 lg:place-self-end max-lg:justify-between'
        }
      >
        <div className="flex gap-2">
          <CustomModal
            triggerComponent={
              <div className="flex gap-1 lg:gap-2 border boder-fadedgrey rounded-full py-3 px-6">
                <ListDashesIcon color="black" />
                <span className="label"> Visible List</span>
              </div>
            }
            handleClick={handleColumnListClick}
            openModal={openColumnListModal}
          >
            <div className="container-sm mx-aut0 10 lg:py-10 py-4 max-lg:w-full">
              <div className="flex flex-col gap-5 lg:gap-10 justify-center items-center">
                <h3>Edit Columns</h3>
                <ColumnList
                  columnList={columnList}
                  selectedColumns={selectedColumns}
                  handleColumnSave={handleColumnSave}
                  handleClose={handleColumnListClick}
                />
              </div>
            </div>
          </CustomModal>
        </div>
        <>
          {!showPageList && !showAddUser && (
            <ExportListMobile
              isOpen={popoverExportMobile}
              exportOptions={exportOptions}
              setPopoverExport={setPopoverExportMobile}
              handleExport={(value) => {
                handleExport(value);
                setPopoverExportMobile(false);
              }}
            />
          )}
          {showPageList && (
            <div className="flex gap-2">
              <PageList
                isOpen={sizeListPopover}
                sizeList={dataSize}
                setPopover={setSizeList}
                defaultValue={dataPerPageCount}
                isDarkTheme={false}
                handlePerPageDataFetch={(value) => {
                  handlePerPageDataFetch(value);
                  setSizeList(false);
                }}
              />
              <PageListMobile
                isOpen={sizeListPopoverMobile}
                sizeList={dataSize}
                setPopover={setSizeListMobile}
                defaultValue={dataPerPageCount}
                isDarkTheme={false}
                handlePerPageDataFetch={(value) => {
                  handlePerPageDataFetch(value);
                  setSizeListMobile(false);
                }}
              />
              <ExportListMobile
                isOpen={popoverExportMobile}
                exportOptions={exportOptions}
                setPopoverExport={setPopoverExportMobile}
                handleExport={(value) => {
                  handleExport(value);
                  setPopoverExportMobile(false);
                }}
              />
            </div>
          )}

          <ExportList
            isOpen={popoverExport}
            exportOptions={exportOptions}
            setPopoverExport={setPopoverExport}
            handleExport={(value) => {
              handleExport(value);
              setPopoverExport(false);
            }}
          />

          {showAddUser && (
            <div className="flex gap-3 items-center">
              <ExportListMobile
                isOpen={popoverExportMobile}
                exportOptions={exportOptions}
                setPopoverExport={setPopoverExportMobile}
                handleExport={handleExport}
              />
              <CustomModal
                triggerComponent={
                  <div className="flex items-center gap-3">
                    <span className="max-lg:hidden text-primary-button">
                      Προσθήκη Χρήστη
                    </span>
                    <PlusIcon className="w-8 h-8" />
                  </div>
                }
                wrapperClass="grid max-h:h-full"
                handleClick={(value) => setOpenModal(value)}
                openModal={openModal}
              >
                <div className="container-sm lg:mx-auto flex flex-col items-center lg:gap-y-5 max-lg:px-5">
                  <div className="flex gap-3 items-center flex-col w-full bg-white lg:py-10 py-5 lg:pb-6 lg:px-6 rounded-t-[20px]">
                    <h3 className="max-lg:pe-3 max-lg:place-self-start">
                      Προσθήκη Νέου Χρήστη
                    </h3>
                    {hasAdminRole && (
                      <div className="lg:px-6 ps-1 py-3 flex lg:items-center lg:justify-center max-lg:gap-1 lg:gap-3 bg-powdergrey max-lg:w-full rounded-xl	">
                        <InfoIcon />
                        <span className="text-body">
                          If you wish to import multiple users
                        </span>
                        <button
                          type="button"
                          className="text-link text-primary"
                          onClick={() => {
                            setOpenModal(false);
                            handleAddMultipleUsers();
                          }}
                        >
                          Click here
                        </button>
                      </div>
                    )}
                  </div>
                  <UserForm handleClose={() => { setOpenModal(false); onUserAdded(); }} />
                </div>
              </CustomModal>
            </div>
          )}
        </>
      </div>
    </div>
  );
};
